/**
 * HTML sanitizer for Telegram-compatible HTML preview.
 * 
 * Allows only the HTML tags that Telegram supports, stripping
 * any potentially dangerous content to prevent XSS attacks.
 */

/** Tags allowed in Telegram HTML */
const ALLOWED_TAGS = new Set([
  'b', 'i', 'u', 's', 'code', 'pre', 'a',
  'blockquote', 'tg-spoiler', 'tg-emoji',
]);

/** Tags that are dangerous and should be completely removed (including content) */
const DANGEROUS_TAGS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea',
]);

/** Allowed attributes per tag */
const ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
  a: new Set(['href']),
  code: new Set(['class']),
  blockquote: new Set(['expandable']),
  'tg-emoji': new Set(['emoji-id']),
};

/** Allowed URL protocols for href attributes */
const ALLOWED_PROTOCOLS = ['http:', 'https:', 'tg:'];

/** Pattern for valid language class on code elements */
const LANGUAGE_CLASS_PATTERN = /^language-[\w-]+$/;

/**
 * Checks if a URL has an allowed protocol.
 */
function isAllowedUrl(url: string): boolean {
  try {
    const parsed = new URL(url, 'https://example.com');
    return ALLOWED_PROTOCOLS.includes(parsed.protocol);
  } catch {
    // Relative URLs are not allowed for links
    return false;
  }
}

/**
 * Sanitizes a class attribute value, keeping only valid language-* classes.
 */
function sanitizeClass(value: string): string | null {
  const classes = value.split(/\s+/).filter(c => LANGUAGE_CLASS_PATTERN.test(c));
  return classes.length > 0 ? classes.join(' ') : null;
}

/**
 * Recursively sanitizes a DOM node, removing disallowed tags and attributes.
 */
function sanitizeNode(node: Node, doc: Document): Node | null {
  if (node.nodeType === Node.TEXT_NODE) {
    return doc.createTextNode(node.textContent ?? '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = node as Element;
  const tagName = element.tagName.toLowerCase();

  // Completely remove dangerous tags and their content
  if (DANGEROUS_TAGS.has(tagName)) {
    return null;
  }

  // Remove disallowed tags, but keep their text content
  if (!ALLOWED_TAGS.has(tagName)) {
    const fragment = doc.createDocumentFragment();
    for (const child of Array.from(element.childNodes)) {
      const sanitizedChild = sanitizeNode(child, doc);
      if (sanitizedChild) {
        fragment.appendChild(sanitizedChild);
      }
    }
    return fragment;
  }

  // Create a clean element
  const cleanElement = doc.createElement(tagName);
  const allowedAttrs = ALLOWED_ATTRIBUTES[tagName] ?? new Set();

  // Copy only allowed attributes
  for (const attr of Array.from(element.attributes)) {
    const attrName = attr.name.toLowerCase();

    // Block all event handlers
    if (attrName.startsWith('on')) {
      continue;
    }

    if (!allowedAttrs.has(attrName)) {
      continue;
    }

    let value = attr.value;

    // Special handling for specific attributes
    if (attrName === 'href') {
      if (!isAllowedUrl(value)) {
        continue; // Skip invalid URLs
      }
    } else if (attrName === 'class' && tagName === 'code') {
      const sanitized = sanitizeClass(value);
      if (!sanitized) {
        continue;
      }
      value = sanitized;
    } else if (attrName === 'expandable') {
      // Boolean attribute - just set it
      cleanElement.setAttribute(attrName, '');
      continue;
    }

    cleanElement.setAttribute(attrName, value);
  }

  // Recursively sanitize children
  for (const child of Array.from(element.childNodes)) {
    const sanitizedChild = sanitizeNode(child, doc);
    if (sanitizedChild) {
      cleanElement.appendChild(sanitizedChild);
    }
  }

  return cleanElement;
}

/**
 * Sanitizes Telegram HTML to prevent XSS attacks.
 * 
 * Only allows the following tags:
 * - b, i, u, s (text formatting)
 * - code, pre (code blocks)
 * - a (links, with href validation)
 * - blockquote (with optional expandable attribute)
 * - tg-spoiler, tg-emoji (Telegram custom elements)
 * 
 * All event handlers and dangerous attributes are stripped.
 * URLs are validated to only allow http:, https:, and tg: protocols.
 * 
 * @param html - Raw HTML string to sanitize
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export function sanitizeTelegramHTML(html: string): string {
  if (!html) return '';

  // Use DOMParser to parse the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const container = doc.body.firstChild;

  if (!container) return '';

  // Create a new document fragment for the sanitized content
  const fragment = document.createDocumentFragment();

  for (const child of Array.from(container.childNodes)) {
    const sanitized = sanitizeNode(child, document);
    if (sanitized) {
      fragment.appendChild(sanitized);
    }
  }

  // Serialize the fragment back to HTML
  const tempDiv = document.createElement('div');
  tempDiv.appendChild(fragment);
  return tempDiv.innerHTML;
}
